import { useState, useEffect, useCallback } from 'react';
import { getInitialSparks } from '../utils/mockData';

const STORAGE_KEY = 'bellow_sparks';

// Fallback UUID generator for browsers that don't support crypto.randomUUID()
function generateUUID() {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers/non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

export function useSparks() {
  const [sparks, setSparks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load sparks from localStorage on mount
  useEffect(() => {
    const loadSparks = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          setSparks(JSON.parse(stored));
        } else {
          // First time user - load mock data
          const initialSparks = getInitialSparks();
          setSparks(initialSparks);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(initialSparks));
        }
      } catch (error) {
        console.error('Error loading sparks:', error);
        setSparks([]);
      } finally {
        setLoading(false);
      }
    };

    loadSparks();
  }, []);

  // Save sparks to localStorage whenever they change
  useEffect(() => {
    if (!loading) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sparks));
    }
  }, [sparks, loading]);

  const addSpark = useCallback((newSpark) => {
    const spark = {
      ...newSpark,
      id: generateUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completed: typeof newSpark.completed === 'boolean' ? newSpark.completed : false,
    };
    setSparks((prev) => [spark, ...prev]);
    return spark;
  }, []);

  const updateSpark = useCallback((id, updates) => {
    setSparks((prev) =>
      prev.map((spark) =>
        spark.id === id
          ? { ...spark, ...updates, updatedAt: new Date().toISOString() }
          : spark
      )
    );
  }, []);

  const deleteSpark = useCallback((id) => {
    setSparks((prev) => prev.filter((spark) => spark.id !== id));
  }, []);

  const getSparkById = useCallback(
    (id) => sparks.find((spark) => spark.id === id),
    [sparks]
  );

  return {
    sparks,
    loading,
    addSpark,
    updateSpark,
    deleteSpark,
    getSparkById,
  };
}

export default useSparks;
